import { Progress, Space, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatBytes } from '@/shared/lib/format'

interface RemainingTrafficProps {
  /** `false` = paket umuman yo'q — "cheksiz" bilan ARALASHTIRILMAYDI */
  hasGrant?: boolean
  /**
   * Kvota. `null`/`undefined` = hajm chegarasi YO'Q.
   *
   * Berilgan bo'lsa to'liq ko'rinish chiziladi (nisbat + progress).
   */
  quotaBytes?: number | null
  consumedBytes?: number | null
  /**
   * FAQAT qoldiq ma'lum bo'lgan joy uchun — abonentlar ro'yxati.
   *
   * ⚠️ `GET /api/subscribers` qatorida `quotaBytes`/`consumedBytes` YO'Q,
   * faqat `remainingBytes` bor (`SubscriberPage.SubscriberRow`). Nisbatni
   * ikkitasidan "tiklashga" urinish (masalan kvotani qoldiq deb olish)
   * ekranda 0% ko'rsatardi va bu ma'lumot emas, yolg'on aniqlik bo'lardi.
   */
  remainingBytes?: number | null
  /** Jadval katagi uchun: progress chizilmaydi, bitta qatorga sig'adi */
  compact?: boolean
}

/**
 * "Paketimdan qancha qoldi?" — abonent eng ko'p beradigan savol.
 *
 * <h2>⚠️ UCH HOLAT, va ularni aralashtirish qimmat</h2>
 * <table border="1">
 *   <caption>Nima ko'rsatiladi</caption>
 *   <tr><th>Holat</th><th>Ekranda</th></tr>
 *   <tr><td>Paket yo'q</td><td>{@code —}</td></tr>
 *   <tr><td>Kvota {@code null}</td><td>"Cheksiz"</td></tr>
 *   <tr><td>Kvota bor</td><td>"12,4 GB qoldi" + "37,6 / 50 GB" + progress</td></tr>
 * </table>
 *
 * Ikkinchi holatni "0 GB" deb chizish eng qimmat xato bo'lardi: jonli
 * tizimdagi abonentlarning HAMMASI hozir kvotasiz, ya'ni butun ro'yxat
 * "trafigi tugagan" bo'lib ko'rinardi va operator asossiz to'lov
 * so'rardi. Shu sababli {@code null} hech qachon songa aylantirilmaydi.
 *
 * <h2>⚠️ Bu "trafik krediti" EMAS</h2>
 * Ledger'dagi {@code trafficLedgerBytes} — oldindan sotib olingan bayt
 * krediti va u BOSHQA narsa (hozir hamma uchun nol). Abonent "paketimdan
 * qancha qoldi" deb so'raganda javob AMALDAGI GRANT holatida, ya'ni shu
 * yerda.
 */
export function RemainingTraffic({
  hasGrant = true,
  quotaBytes,
  consumedBytes,
  remainingBytes,
  compact = false,
}: RemainingTrafficProps) {
  const { t } = useTranslation()

  if (!hasGrant) {
    return (
      <Tooltip title={t('billing.quota.noGrantHint')}>
        <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
      </Tooltip>
    )
  }

  // --- Kvota ma'lum: to'liq ko'rinish ---------------------------------------
  if (quotaBytes !== null && quotaBytes !== undefined) {
    const used = Math.max(0, consumedBytes ?? 0)
    // ⚠️ Manfiy bo'lmasin: hisob kechikishi sababli sarflangan hajm kvotadan
    // bir oz oshib ketishi mumkin (`UsageAccountingService` uni keyin
    // qirqadi). Ekranda "-2 GB qoldi" chiqishi nuqsonga o'xshardi.
    const remaining = Math.max(0, quotaBytes - used)
    const percent = quotaBytes > 0 ? Math.min(100, Math.round((used / quotaBytes) * 100)) : 100
    const exhausted = remaining === 0
    const ratio = `${formatBytes(used)} / ${formatBytes(quotaBytes)}`

    if (compact) {
      return (
        <Tooltip title={`${ratio} · ${percent}%`}>
          <Typography.Text type={exhausted ? 'danger' : undefined}>
            {exhausted ? t('billing.quota.exhausted') : formatBytes(remaining)}
          </Typography.Text>
        </Tooltip>
      )
    }

    return (
      <Space direction="vertical" size={2} style={{ width: '100%', minWidth: 180 }}>
        <Typography.Text strong type={exhausted ? 'danger' : undefined}>
          {exhausted
            ? t('billing.quota.exhausted')
            : t('billing.quota.remainingValue', { value: formatBytes(remaining) })}
        </Typography.Text>
        <Progress
          percent={percent}
          size="small"
          // 90% dan oshgani — "tugash arafasida" signali: operator sonni
          // o'qimasdan ham holatni ko'radi.
          status={exhausted ? 'exception' : percent >= 90 ? 'active' : 'normal'}
          format={(value) => `${value ?? 0}%`}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {ratio}
        </Typography.Text>
      </Space>
    )
  }

  // --- Faqat qoldiq ma'lum (ro'yxat qatori) --------------------------------
  if (remainingBytes !== null && remainingBytes !== undefined) {
    return remainingBytes === 0 ? (
      <Typography.Text type="danger">{t('billing.quota.exhausted')}</Typography.Text>
    ) : (
      <Typography.Text>{formatBytes(remainingBytes)}</Typography.Text>
    )
  }

  return (
    <Tooltip title={t('billing.quota.unlimitedHint')}>
      <Typography.Text type="secondary">{t('billing.unlimited')}</Typography.Text>
    </Tooltip>
  )
}
